import {convertScalarSecondsToTimestep} from '../../../util/plotHelpers/time';
import {type Timestep} from '../../../util/plotHelpers/types';
import {type BrushEndMetadata} from '../../vis/highlight';
import {type DomainMaybe} from '../../vis/types';
import {
  getZoomUsageType,
  type LinePlotZoomUsageEvent,
} from '../observability/zoomUsage';
import {type PartialZoom, type UserZoomIntentPayload} from '../types';
import {
  getZoomIntent,
  isResetZoomIntent,
  shouldPropagateSharedXAxisZoom,
} from './zoomIntent';

/** A domain with either bound set, or undefined when neither bound is finite. */
export function toDomain(
  min: number | null | undefined,
  max: number | null | undefined
): DomainMaybe | undefined {
  if (Number.isFinite(min) || Number.isFinite(max)) {
    return [min ?? null, max ?? null];
  }
  return undefined;
}

/** What each zoom system offers. Graph uses one, chosen by fidelity mode. */
export type ZoomSources = {
  fullFidelity: {
    handleUserZoom: (zoom: UserZoomIntentPayload) => void;
    queryZoom: PartialZoom;
    zooming: boolean;
  };
  isFullFidelity: boolean;
  sampling: {
    handleUserZoomChange: (zoom: UserZoomIntentPayload) => void;
    xDomainChart: DomainMaybe | undefined;
    yDomain: DomainMaybe | undefined;
    zooming: boolean;
  };
};

export type ZoomState = {
  /** X domain in history units (seconds for time axes). */
  historySpaceXDomain: DomainMaybe | undefined;
  updateUserZoom: (zoom: UserZoomIntentPayload) => void;
  yDomain: DomainMaybe | undefined;
  zooming: boolean;
};

/**
 * Picks the zoom system for the fidelity mode. The two systems keep their own
 * rules: full fidelity derives both domains from the query zoom; sampling uses
 * the chart x domain and its own y domain.
 */
export function selectZoomState({
  fullFidelity,
  isFullFidelity,
  sampling,
}: ZoomSources): ZoomState {
  if (isFullFidelity) {
    const {queryZoom} = fullFidelity;
    return {
      historySpaceXDomain: toDomain(queryZoom.xAxisMin, queryZoom.xAxisMax),
      updateUserZoom: fullFidelity.handleUserZoom,
      yDomain: toDomain(queryZoom.yAxisMin, queryZoom.yAxisMax),
      zooming: fullFidelity.zooming,
    };
  }
  return {
    historySpaceXDomain: toDomain(
      sampling.xDomainChart?.[0],
      sampling.xDomainChart?.[1]
    ),
    updateUserZoom: sampling.handleUserZoomChange,
    yDomain: sampling.yDomain,
    zooming: sampling.zooming,
  };
}

/**
 * Converts a history-space x domain to the chart's x units. A line timestep
 * wins over a datetime axis; null bounds stay null.
 */
export function toChartXDomain(
  domain: DomainMaybe | undefined,
  {
    timestep,
    xAxisIsDatetime,
  }: {timestep: Timestep | undefined; xAxisIsDatetime: boolean}
): DomainMaybe | undefined {
  if (domain == null) {
    return domain;
  }
  if (timestep) {
    return [
      domain[0] == null
        ? domain[0]
        : convertScalarSecondsToTimestep(domain[0], timestep),
      domain[1] == null
        ? domain[1]
        : convertScalarSecondsToTimestep(domain[1], timestep),
    ];
  }
  if (xAxisIsDatetime) {
    return [
      domain[0] == null ? domain[0] : domain[0] * 1000,
      domain[1] == null ? domain[1] : domain[1] * 1000,
    ];
  }
  return domain;
}

/** True when either bound of either domain is set. */
export function hasActiveZoom(
  xDomain: DomainMaybe | undefined,
  yDomain: DomainMaybe | undefined
): boolean {
  return (
    (Array.isArray(xDomain) && (xDomain[0] != null || xDomain[1] != null)) ||
    (Array.isArray(yDomain) && (yDomain[0] != null || yDomain[1] != null))
  );
}

/** 'keep' means leave the current value alone. */
export type ZoomEffects = {
  shared: {xMax: number | null; xMin: number | null} | null;
  timeFactor: Timestep | null | 'keep';
  usage: LinePlotZoomUsageEvent | null;
  zooming: boolean | 'keep';
};

/**
 * Side effects of a zoom gesture, decided without performing them.
 * - Shared x-axis zoom: x zooms and resets propagate; y-only zooms do not.
 * - Time factor: a reset clears it; an x zoom sets it to the line timestep.
 * - Zooming flag: sampling mode only; full fidelity tracks zooming itself.
 * - Usage telemetry: brushed gestures only. A reset counts only if something
 *   was zoomed before the gesture. Gestures the zoom state later rejects are
 *   still recorded, since telemetry records what the user did.
 */
export function planZoomEffects(
  zoom: UserZoomIntentPayload,
  {
    activeZoom,
    brushEndMetadata,
    isFullFidelity,
    timestep,
  }: {
    activeZoom: boolean;
    brushEndMetadata?: BrushEndMetadata;
    isFullFidelity: boolean;
    timestep: Timestep | undefined;
  }
): ZoomEffects {
  const intent = getZoomIntent(zoom);
  const usageType = getZoomUsageType(intent);
  const isReset = isResetZoomIntent(zoom);

  let timeFactor: ZoomEffects['timeFactor'] = 'keep';
  if (isReset) {
    timeFactor = null;
  } else if (intent.shouldZoomX) {
    timeFactor = timestep ?? null;
  }

  const shouldRecordUsage =
    brushEndMetadata != null && (usageType !== 'reset' || activeZoom);

  return {
    shared: shouldPropagateSharedXAxisZoom(zoom)
      ? {xMax: zoom.xAxisMax ?? null, xMin: zoom.xAxisMin ?? null}
      : null,
    timeFactor,
    usage: shouldRecordUsage
      ? {
          source: 'drag',
          type: usageType,
          xZoomPercent: brushEndMetadata.xZoomPercent,
          yZoomPercent: brushEndMetadata.yZoomPercent,
        }
      : null,
    zooming: isFullFidelity ? 'keep' : !isReset,
  };
}
